import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';

import { screenSize } from 'lib/utils';

import useApprovalCreditLimit from 'hooks/approval_credit_limit/useApprovalCreditLimit';

import ApprovalCreditLimitContent from './approval_credit_limit_content';
import ApprovalCreditLimitTooltip from './approval_credit_limit_tooltip';

/**
 * Renders a <ApprovalCreditLimit/> component
 * @param props
 * @param props.useDataEndpoint - to fetch the latest User Credit Limit from API
 * @param props.isProductPage - - to determine if component is rendered on the product show page
 */
const ApprovalCreditLimit = ({ useDataEndpoint, isProductPage }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [dismiss, setDismiss] = useState(false);
  const [loading, setLoading] = useState(true);
  const [hoverTimeout, setHoverTimeout] = useState(null);

  const [creditLimitMessage, setCreditLimitMessage] = useState(null);

  const { response, setUrl } = useApprovalCreditLimit();

  /**
   * Toggle the tooltip state
   * @param {Object} event - the JS event object
   * @returns false - if screen size is larger than 1024px
   */
  const toggleTooltip = event => {
    event.preventDefault();

    if (screenSize() >= 1025) {
      return false;
    }

    setShowTooltip(!showTooltip);
  };

  /**
   * Keypress handler to dismiss the tooltip
   * @param {Object} event - JS event object
   */
  const handleDismiss = event => {
    const { key } = event;
    if (key === 'Escape') {
      setDismiss(true);
    }
  };

  /**
   * Hover event handler for desktop
   * Sets tooltip--show class after a .25 second delay
   */
  const handleHover = () => {
    if (screenSize() < 1025) {
      return false;
    }

    setHoverTimeout(
      setTimeout(() => {
        setShowTooltip(true);
      }, 250),
    );
  };

  /**
   * MouseLeave event handler to hide the tooltip class
   * Clears the timeout
   */
  const handleLeave = () => {
    if (screenSize() < 1025) {
      return false;
    }

    setShowTooltip(false);
    clearTimeout(hoverTimeout);
  };

  /**
   * Hiding wrappers & unmount React component from DOM on API Fail
   * Very unlikely scenario
   */
  const unmountAllInstances = () => {
    const nodes = document.querySelectorAll('.js-approval-credit-limit');
    for (let i = 0; i < nodes.length; i++) {
      const node = nodes[i];
      node.classList.add('oa-credit-wrapper--hidden');
      ReactDOM.unmountComponentAtNode(node);
    }
  };

  // instances that do not use the API endpoint
  // should subscribe to the pubsub event to get the latest credit limit data
  useEffect(() => {
    if (!useDataEndpoint) {
      PubSub.subscribe(
        'UPDATE-CREDIT-LIMIT-INSTANCES',
        (_msg, creditLimitMsg) => {
          setCreditLimitMessage(creditLimitMsg);
          setLoading(false);
        },
      );
    }
  }, []);

  // Fetching latest credit limit using API endpoint
  // listen for future events to fetch the latest data
  useEffect(() => {
    if (useDataEndpoint) {
      setUrl(useDataEndpoint);

      PubSub.subscribe('FETCH-USER-CREDIT-LIMIT', () =>
        setUrl(useDataEndpoint),
      );
    }
  }, [useDataEndpoint]);

  // Listening for response to User Credit Limit endpoint
  useEffect(() => {
    if (response.result) {
      const { success, data } = response.result;
      if (success) {
        const {
          message: {
            title,
            subtitle,
            page_show_title: pageShowTitle,
            more_info: tooltipText,
          },
          value,
        } = data;

        const newMessage = {
          title,
          subtitle,
          pageShowTitle,
          tooltipText,
          creditLimit: value,
        };

        setLoading(false);
        setCreditLimitMessage(newMessage);

        PubSub.publish('UPDATE-CREDIT-LIMIT-INSTANCES', newMessage);
      } else {
        setLoading(false);
        unmountAllInstances();
      }

      setUrl(null);
    }
  }, [response.result]);

  const { creditLimit, tooltipText } = creditLimitMessage || '';
  const loadingClass = loading ? 'oa-credit-limit--is-loading' : '';
  const showTooltipClass = showTooltip ? 'oa-credit-limit--show-tooltip' : '';

  let borderClass;
  if (creditLimit) {
    borderClass =
      creditLimit >= 0
        ? 'oa-credit-limit--border-green'
        : 'oa-credit-limit--border-yellow';
  } else {
    borderClass = 'oa-credit-limit--border-regular';
  }

  return (
    <button
      data-testid="oa-credit-limit"
      type="button"
      className={`oa-credit-limit ${borderClass} ${showTooltipClass} ${loadingClass}`}
      onClick={e => !loading && toggleTooltip(e)}
      onKeyDown={e => !loading && handleDismiss(e)}
      onMouseEnter={() => !loading && handleHover()}
      onMouseLeave={() => !loading && handleLeave()}>
      {creditLimitMessage && !loading && (
        <ApprovalCreditLimitContent
          message={creditLimitMessage}
          isProductPage={isProductPage}
        />
      )}
      {tooltipText && !loading && (
        <ApprovalCreditLimitTooltip text={tooltipText} dismiss={dismiss} />
      )}
      <span className="oa-credit-limit__loader loader no-margin basket" />
    </button>
  );
};

export default ApprovalCreditLimit;
